import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from './supabase';
import { supabaseData } from './supabaseData';
import { useTheme } from './ThemeContext';

// Feature definitions by tier
const TIER_FEATURES = {
  free: {
    maxCalendars: 1,
    maxUsers: 1,
    maxPurchasableSeats: 0,
    maxCustomers: 25, // Free tier is capped; upgrade removes the cap
    texting: false,
    advancedReporting: false,
    apiAccess: false,
    customBranding: false,
    teamManagement: false,
    prioritySupport: false,
    commissions: false,
    flexWorkers: false,
    flexDaysIncluded: 0
  },
  pro: {
    maxCalendars: 1,
    maxUsers: 1,
    maxPurchasableSeats: 2, // Can buy up to 2 extra = 3 total
    texting: false,
    advancedReporting: false,
    apiAccess: false,
    customBranding: false,
    teamManagement: false,
    prioritySupport: false,
    commissions: false,
    flexWorkers: false,
    flexDaysIncluded: 0
  },
  team: {
    maxCalendars: 5,
    maxUsers: 3, // 3 included (owner + 2 team members)
    maxPurchasableSeats: 2, // Can buy up to 2 extra = 5 total
    texting: true,
    advancedReporting: true,
    apiAccess: false,
    customBranding: false,
    teamManagement: true,
    prioritySupport: true,
    commissions: true,
    flexWorkers: false,
    flexDaysIncluded: 0
  },
  growth: {
    maxCalendars: 999,
    maxUsers: 5, // 5 included
    maxPurchasableSeats: 5, // Can buy up to 5 extra = 10 total
    texting: true,
    advancedReporting: true,
    apiAccess: true,
    customBranding: true,
    teamManagement: true,
    prioritySupport: true,
    commissions: true,
    flexWorkers: false,
    flexDaysIncluded: 0
  },
  business: {
    maxCalendars: 999,
    maxUsers: 10, // 10 included
    maxPurchasableSeats: 10, // Can buy up to 10 extra = 20 total
    texting: true,
    advancedReporting: true,
    apiAccess: true,
    customBranding: true,
    teamManagement: true,
    prioritySupport: true,
    commissions: true,
    flexWorkers: true,
    flexDaysIncluded: 30 // 30 flex-worker days per month
  },
  enterprise: {
    maxCalendars: 999,
    maxUsers: 999, // Unlimited
    maxPurchasableSeats: 999,
    texting: true,
    advancedReporting: true,
    apiAccess: true,
    customBranding: true,
    teamManagement: true,
    prioritySupport: true,
    commissions: true,
    flexWorkers: true,
    flexDaysIncluded: 999 // Unlimited flex days
  },
  // Legacy alias - redirect to business
  unlimited: {
    maxCalendars: 999,
    maxUsers: 5,
    maxPurchasableSeats: 5,
    texting: true,
    advancedReporting: true,
    apiAccess: true,
    customBranding: true,
    teamManagement: true,
    prioritySupport: true,
    commissions: true,
    flexWorkers: false,
    flexDaysIncluded: 0
  }
};

const FeatureContext = createContext(null);

export function FeatureProvider({ children, companyId }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(0);

  useEffect(() => {
    if (companyId) {
      loadCompanyData();
    } else {
      // No company yet, stop loading
      setLoading(false);
    }
  }, [companyId]);

  const loadCompanyData = async () => {
    try {
      // Load company info
      const { data: companyData, error: companyError } = await supabaseData
        .from('companies')
        .select(`
          id,
          tier,
          max_calendars,
          max_users,
          purchased_seats,
          seat_price,
          texting_enabled,
          recurring_billing_enabled,
          advanced_reporting_enabled,
          api_access_enabled,
          custom_branding_enabled,
          business_type,
          subscription_status,
          trial_ends_at,
          is_demo
        `)
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;

      // Load team member count
      const { count, error: countError } = await supabaseData
        .from('user_companies')
        .select('*', { count: 'exact', head: true })
        .eq('company_id', companyId);

      if (countError) throw countError;

      // Self-healing: if no user_companies records but company has an owner, create the owner record
      if ((count === 0 || count === null) && companyData?.owner_user_id) {
        const { error: insertError } = await supabaseData
          .from('user_companies')
          .upsert({
            user_id: companyData.owner_user_id,
            company_id: companyId,
            role: 'owner'
          }, { onConflict: 'user_id,company_id' });

        if (insertError) {
        } else {
          // Re-count after creating owner record
          const { count: newCount } = await supabaseData
            .from('user_companies')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', companyId);
          setTeamCount(newCount || 1);
          setCompany(companyData);
          return;
        }
      }

      setCompany(companyData);
      setTeamCount(count || 1);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Get tier-based features
  // Demo accounts get enterprise-level features regardless of tier
  const getTierFeatures = () => {
    if (!company) return TIER_FEATURES.pro;
    if (company.is_demo) return TIER_FEATURES.enterprise;
    return TIER_FEATURES[company.tier] || TIER_FEATURES.pro;
  };

  // Check if a feature is enabled
  const hasFeature = (featureName) => {
    if (!company) return false;

    // Check database override first (some features can be explicitly set)
    switch (featureName) {
      case 'texting':
        return company.texting_enabled ?? getTierFeatures().texting;
      case 'recurringBilling':
        return company.recurring_billing_enabled ?? false;
      case 'advancedReporting':
        return company.advanced_reporting_enabled ?? getTierFeatures().advancedReporting;
      case 'apiAccess':
        return company.api_access_enabled ?? getTierFeatures().apiAccess;
      case 'customBranding':
        return company.custom_branding_enabled ?? getTierFeatures().customBranding;
      case 'teamManagement':
        return getTierFeatures().teamManagement;
      case 'prioritySupport':
        return getTierFeatures().prioritySupport;
      case 'commissions':
        return getTierFeatures().commissions;
      default:
        return false;
    }
  };

  // Get limits
  const getLimit = (limitName) => {
    if (!company) return 1;

    switch (limitName) {
      case 'maxCalendars':
        return company.max_calendars ?? getTierFeatures().maxCalendars;
      case 'maxUsers':
        // Base users from plan + any purchased extra seats
        const baseUsers = company.max_users ?? getTierFeatures().maxUsers;
        const purchasedSeats = company.purchased_seats ?? 0;
        return baseUsers + purchasedSeats;
      case 'baseUsers':
        // Just the plan limit without purchased seats
        return company.max_users ?? getTierFeatures().maxUsers;
      case 'purchasedSeats':
        return company.purchased_seats ?? 0;
      case 'maxPurchasableSeats':
        return getTierFeatures().maxPurchasableSeats;
      case 'maxCustomers':
        // Only the free tier is capped; every paid tier is effectively unlimited.
        return getTierFeatures().maxCustomers ?? 999999;
      default:
        return 0;
    }
  };

  // Check if user can purchase more seats (Pro plan has limit of 4 extra)
  const canPurchaseMoreSeats = () => {
    const currentPurchased = company?.purchased_seats ?? 0;
    const maxPurchasable = getTierFeatures().maxPurchasableSeats;
    return currentPurchased < maxPurchasable;
  };

  // Get remaining purchasable seats
  const getRemainingPurchasableSeats = () => {
    const currentPurchased = company?.purchased_seats ?? 0;
    const maxPurchasable = getTierFeatures().maxPurchasableSeats;
    return Math.max(0, maxPurchasable - currentPurchased);
  };

  // Get seat pricing info
  const getSeatPrice = () => {
    return company?.seat_price ?? 5.00;
  };

  // Check if a limit is reached
  const isLimitReached = (limitName, currentCount) => {
    const limit = getLimit(limitName);
    return currentCount >= limit;
  };

  // Check if user can add more team members
  const canAddTeamMember = () => {
    const maxUsers = getLimit('maxUsers');
    return teamCount < maxUsers;
  };

  // Get remaining slots
  const getRemainingSlots = (limitName, currentCount) => {
    const limit = getLimit(limitName);
    return Math.max(0, limit - currentCount);
  };

  // Check trial status (demo accounts are never trialing/expired)
  const isTrialing = () => {
    if (company?.is_demo) return false;
    return company?.subscription_status === 'trialing';
  };

  const isTrialExpired = () => {
    if (company?.is_demo) return false;
    if (!company?.trial_ends_at) return false;
    return new Date(company.trial_ends_at) < new Date();
  };

  // Get tier name for display
  const getTierName = () => {
    if (!company) return 'Pro';
    switch (company.tier) {
      case 'free': return 'Free';
      case 'pro': return 'Pro';
      case 'team': return 'Team';
      case 'business': return 'Business';
      case 'unlimited': return 'Business'; // Legacy alias
      case 'growth': return 'Growth';
      case 'enterprise': return 'Enterprise';
      default: return 'Pro';
    }
  };

  // Get business type
  const getBusinessType = () => {
    return company?.business_type || 'garage_door';
  };

  // Refresh data
  const refresh = () => {
    if (companyId) {
      loadCompanyData();
    }
  };

  // Flex worker support
  const hasFlexWorkers = () => {
    return getTierFeatures().flexWorkers === true;
  };

  const getFlexDaysIncluded = () => {
    return getTierFeatures().flexDaysIncluded || 0;
  };

  const value = {
    loading,
    company,
    tier: company?.tier || 'pro',
    isDemo: company?.is_demo || false,
    tierName: getTierName(),
    businessType: getBusinessType(),
    teamCount,
    purchasedSeats: getLimit('purchasedSeats'),
    seatPrice: getSeatPrice(),

    // Feature checks
    hasFeature,

    // Limits
    getLimit,
    isLimitReached,
    canAddTeamMember,
    getRemainingSlots,
    getSeatPrice,
    canPurchaseMoreSeats,
    getRemainingPurchasableSeats,

    // Trial
    isTrialing,
    isTrialExpired,

    // Flex workers
    hasFlexWorkers,
    getFlexDaysIncluded,

    // Utility
    refresh
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeatureProvider');
  }
  return context;
}

// Component for gating features
export function FeatureGate({ feature, fallback = null, children }) {
  const { hasFeature, loading } = useFeatures();

  if (loading) return null;
  if (!hasFeature(feature)) return fallback;

  return children;
}

// Component for showing upgrade prompts
export function UpgradePrompt({ feature, requiredTier = 'team', onUpgrade, onBack, currentTier }) {
  const { tierName } = useFeatures();
  const { colors } = useTheme();
  const displayTier = currentTier
    ? currentTier.charAt(0).toUpperCase() + currentTier.slice(1)
    : tierName;

  const featureNames = {
    texting: 'SMS Texting',
    recurringBilling: 'Recurring Billing',
    advancedReporting: 'Advanced Reporting',
    apiAccess: 'API Access',
    customBranding: 'Custom Branding',
    teamManagement: 'Team Management',
    commissions: 'Commission Tracking'
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)',
      border: '1px solid #F05A28',
      borderRadius: '12px',
      padding: '1.5rem',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
        ✨
      </div>
      <h3 style={{ color: '#e0e0e0', margin: '0 0 0.5rem 0' }}>
        {featureNames[feature] || 'This Feature'} is a {requiredTier.charAt(0).toUpperCase() + requiredTier.slice(1)} Feature
      </h3>
      <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '1rem' }}>
        You're on the {displayTier} plan. Upgrade to unlock {featureNames[feature]?.toLowerCase() || 'this feature'}.
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
        {onBack && (
          <button
            onClick={onBack}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: '#888',
              border: '1px solid #3a3a3a',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Go Back
          </button>
        )}
        {onUpgrade && (
          <button
            onClick={onUpgrade}
            style={{
              padding: '0.75rem 1.5rem',
              background: colors.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
          >
            Upgrade Now
          </button>
        )}
      </div>
    </div>
  );
}

export default FeatureContext;
